import { showToastError, showToastSuccess } from '../../messages'
import DropdownMenu from '../../stories/DropdownMenu'
import { IconWrapper } from '../../stories/IconWrapper'
import { Share06 } from '@untitled-ui/icons-react'
import { t } from '@lingui/core/macro'
import { useState } from 'react'

type BadgeShareLinks = {
  linkedInUrl: string
  publicUrl: string
}

export async function getBadgeShareLinks(args: {
  notificationId?: number
  issuedBadgeId?: string
}): Promise<BadgeShareLinks> {
  const params = new URLSearchParams()

  if (typeof args.notificationId === 'number') {
    params.set('notificationId', String(args.notificationId))
  }

  if (typeof args.issuedBadgeId === 'string') {
    params.set('issuedBadgeId', args.issuedBadgeId)
  }

  const response = await fetch(
    `/api/v1/badges/share-link?${params.toString()}`,
    {
      credentials: 'same-origin'
    }
  )

  if (!response.ok) {
    const body = await response.json().catch(() => null)
    throw new Error(body?.error ?? 'Unable to create badge share link')
  }

  return response.json()
}

export function BadgeWinShareMenu({
  notificationId,
  onShared
}: {
  notificationId: number
  onShared?: () => void
}) {
  const [isLoading, setIsLoading] = useState(false)

  return (
    <DropdownMenu
      btnLabel=''
      hierarchy='ghost'
      loadingState={isLoading ? 'loading' : 'idle'}
      items={[
        { id: 'linkedin', label: t`Share on LinkedIn` },
        { id: 'copy', label: t`Copy link` }
      ]}
      customButton={
        <button
          type='button'
          className='inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/20 bg-white/12 text-white transition hover:bg-white/20'
          aria-label={t`Share badge`}>
          <IconWrapper Icon={Share06} size={18} className='text-white' />
        </button>
      }
      onSelectOption={async selection => {
        if (typeof selection === 'string') {
          return
        }

        const selected = Array.from(selection)[0]

        if (!selected || isLoading) {
          return
        }

        try {
          setIsLoading(true)
          const links = await getBadgeShareLinks({ notificationId })

          if (selected === 'copy') {
            await navigator.clipboard.writeText(links.publicUrl)
            showToastSuccess(t`Badge share link copied`)
            onShared?.()
            return
          }

          window.open(links.linkedInUrl, '_blank', 'noopener,noreferrer')
          onShared?.()
        } catch (error) {
          showToastError(
            error instanceof Error ? error : new Error('Unable to share badge')
          )
        } finally {
          setIsLoading(false)
        }
      }}
    />
  )
}
